import type { ResourceFilterStatus } from '@/lib/api/types/teams';
import type { OpenPositionFilledStatus } from '../api/types/capacity/openPositions';
import type { TeamAllocationRequest } from '../api/types/insights/team-allocations';
import type { LocationSearchParams } from '../api/types/location';

export const capacityQueryKeys = {
  resources: {
    all: () => ['resources'],
    detail: (resourceId: number) => [...capacityQueryKeys.resources.all(), 'detail', resourceId],
    search: (filters: {
      query?: string;
      resourceStatus?: ResourceFilterStatus;
      locationIds?: number[];
      roleIds?: number[];
      skillIds?: number[];
    }) => [...capacityQueryKeys.resources.all(), 'search', filters],
  },
  workloadSchemes: {
    all: () => ['workloadSchemes'],
    search: (query: string) => [...capacityQueryKeys.workloadSchemes.all(), 'search', query],
    detail: ({ schemeId }: { schemeId: number }) => [
      ...capacityQueryKeys.workloadSchemes.all(),
      'detail',
      schemeId,
    ],
  },
  holidaySchemes: {
    all: () => ['holidaySchemes'],
    search: (query: string) => [...capacityQueryKeys.holidaySchemes.all(), 'search', query],
    detail: ({ schemeId }: { schemeId: number }) => [
      ...capacityQueryKeys.holidaySchemes.all(),
      'detail',
      schemeId,
    ],
    searchRanges: ({ schemeId, query }: { schemeId: number; query: string }) => [
      ...capacityQueryKeys.holidaySchemes.all(),
      'searchRanges',
      schemeId,
      query,
    ],
  },
  locations: {
    all: () => ['locations'],
    search: (searchParams?: LocationSearchParams) => [
      ...capacityQueryKeys.locations.all(),
      'search',
      searchParams,
    ],
  },
  teamRoles: {
    all: () => ['team-roles'],
    search: (query: string) => [...capacityQueryKeys.teamRoles.all(), 'search', query],
  },
  skills: {
    all: () => ['skills'],
    search: (query: string) => [...capacityQueryKeys.skills.all(), 'search', query],
  },
  openPositions: {
    all: () => ['open-positions'],
    detail: (id: number) => [...capacityQueryKeys.openPositions.all(), 'detail', id],
    search: (filters: {
      query?: string;
      filledStatus?: OpenPositionFilledStatus;
      roleIds?: number[];
      skillIds?: number[];
      locationIds?: number[];
      sortBy?: string;
      sortDirection?: 'ASC' | 'DESC';
    }) => [...capacityQueryKeys.openPositions.all(), 'search', filters],
    countByLoop: (loopId: number) => [...capacityQueryKeys.openPositions.all(), 'count', loopId],
  },
  jiraAuthStatus: {
    all: () => ['jiraAuthStatus'],
  },
  orphanedInstances: {
    all: () => ['orphanedInstances'],
  },
  publicHolidays: {
    all: (year: number, countryCode: string) => ['publicHolidays', year, countryCode] as const,
    availableCountries: () => ['publicHolidays', 'availableCountries'] as const,
  },
  common: {
    schemeMembers: {
      all: () => ['scheme-members'],
      detail: (schemeId: number, query?: string) => ['scheme-members', { schemeId, query }],
    },
    membersNotInScheme: {
      all: () => ['members-not-in-scheme'],
      detail: (schemeId: number, query?: string) => ['members-not-in-scheme', { schemeId, query }],
    },
  },
  teamWorkAllocations: {
    all: () => ['team-work-allocations'],
    byLoopAndDateRange: (request: TeamAllocationRequest) => [
      ...capacityQueryKeys.teamWorkAllocations.all(),
      request,
    ],
  },
};
